// @flow strict
import { number, object, string } from 'yup'
import { type CheckEligibilityRequest, type FormState } from './formTypes'

export const eligibilityCheckerFormSchema = () => {
  const currentYear = new Date().getFullYear()
  return object().shape({
    age: number()
      .min(16, 'You must be at least 16 years old to have a Monzo account')
      .required('This field is required'),
    postcode: string()
      .matches(
        /^(([Gg][Ii][Rr] ?0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) ?[0-9][A-Za-z]{2}))$/,
        'Please enter a valid postcode',
      )
      .required('This field is required'),
    income: number()
      .min(0, 'Please use a positive number')
      .integer('Please round to the nearest pound')
      .required('This field is required'),
    reducedIncome: number()
      .min(0, 'Please use a positive number')
      .integer('Please round to the nearest pound'),
    housingExpenditure: number()
      .min(0, 'Please use a positive number')
      .integer('Please round to the nearest pound'),
    desiredLimit: number()
      .min(0, 'Please use a positive number')
      .integer('Please round to the nearest pound'),
    existingDebt: object().shape({
      overdraftBalance: number()
        .min(0, 'Please use a positive number')
        .integer('Please round to the nearest pound'),
      creditBalance: number()
        .min(0, 'Please use a positive number')
        .integer('Please round to the nearest pound'),
      fixedMonthlyPayments: number()
        .min(0, 'Please use a positive number')
        .integer('Please round to the nearest pound'),
    }),
    hasPreviousCredit: string().oneOf(['Yes', 'No']),
    hasPreviousJudgementOrDefault: string().oneOf(['Yes', 'No']),
    hasMissedPayments: string().oneOf(['Yes', 'No']),
    ukNational: string().oneOf(['Yes', 'No']),
    rightToReside: string().when('ukNational', {
      is: true,
      then: string().required(),
      otherwise: string().nullable(),
    }),
    rightToResideExpiry: number()
      .when('rightToReside', {
        is: 'temporary',
        then: number().required(),
        otherwise: number().nullable(),
      })
      .min(currentYear)
      .max(currentYear + 5)
      .integer('Please provide the year your right to reside expires'),
  })
}

const centify = (pounds: string): number =>
  (pounds && parseInt(pounds) * 100) || 0

export const formStateToEligibilityCheckerRequest = (
  form: FormState,
): CheckEligibilityRequest => {
  return {
    age: parseInt(form.age, 10),
    employment_status: form.employmentStatus,
    postcode: form.postcode,
    residential_status: form.residentialStatus,
    housing_expenditure_amount: centify(
      (form.residentialStatus !== 'no-mortgage' && form.housingExpenditure) ||
        '0',
    ),
    housing_expenditure_currency: 'GBP',
    gross_annual_income_amount: centify(form.income),
    gross_annual_income_currency: 'GBP',
    has_reduced_annual_income: form.reducedIncome !== '',
    reduced_annual_income_amount: centify(form.reducedIncome),
    reduced_annual_income_currency: 'GBP',
    num_dependents: parseInt(form.numDependents, 10),
    existing_debt: {
      revolving_credit_balance_amount:
        centify(form.existingDebt.creditBalance) +
        centify(form.existingDebt.overdraftBalance),
      revolving_credit_balance_currency: 'GBP',
      monthly_fixed_payments_amount: centify(
        form.existingDebt.fixedMonthlyPayments,
      ),
      monthly_fixed_payments_currency: 'GBP',
    },
    has_existing_credit: form.hasPreviousCredit === 'Yes',
    has_ccj_or_defaults: form.hasPreviousJudgementOrDefault === 'Yes',
    has_missed_payments: form.hasMissedPayments === 'Yes',
    uk_national: form.ukNational === 'Yes',
    right_to_reside: form.rightToReside,
    right_to_reside_expiry: form.rightToResideExpiry,
    limit: desiredLimitValues[form.desiredLimit] * 100,
  }
}

export const desiredLimitValues = [250, 500, 750, 1000]
